import { usePathname } from "@-/com.npmjs.expo-router";
import {
  answerOnboardingAnnouncement,
  getOnboardingState,
  playOnboardingReaction,
  useOnboardingState,
} from "@-/dev.improve.haniverse.lib.onboarding";
import {
  getOnboardingAnnouncement,
  hasOnboardingReactionPlayed,
  isOnboardingActive,
  isOnboardingDestinationUnlocked,
  type OnboardingPlace,
} from "@-/dev.improve.haniverse.lib.onboarding.state";
import { useEffect, useRef } from "react";
import { getOnboardingPlace, getOnboardingPlaceTab } from "#onboarding-place";

/**
 * Plays the onboarding's reactions to where the learner goes. Mount once, in the app shell:
 *
 * - Reaching the cards, once Learn is unlocked, ends the tour.
 * - Switching tabs answers the announcement the sheet was showing where the learner was, whether
 *   they followed it or went somewhere else, so it never shows again. A page's own introduction
 *   is the sheet's other subject: walked away from, it waits for the learner to come back.
 */
export function useOnboardingPlaceReactions(): void {
  const state = useOnboardingState();
  const place = getOnboardingPlace(usePathname());
  const isActive = isOnboardingActive(state);
  const shouldReachCards =
    isActive &&
    place === "cards" &&
    isOnboardingDestinationUnlocked(state, "learn") &&
    !hasOnboardingReactionPlayed(state, "cards");
  useEffect(() => {
    if (shouldReachCards) {
      playOnboardingReaction("cards");
    }
  }, [shouldReachCards]);

  const previousPlaceRef = useRef<OnboardingPlace>(place);
  useEffect(() => {
    const previousPlace = previousPlaceRef.current;
    previousPlaceRef.current = place;
    const previousTab = getOnboardingPlaceTab(previousPlace);
    const tab = getOnboardingPlaceTab(place);
    // Only a move between tabs: not within one (the map and the cards are both Learn's), nor to
    // or from a page outside the strip (the launch's index redirect, a sign-in).
    if (previousTab === null || tab === null || previousTab === tab) {
      return;
    }
    const destination = getOnboardingAnnouncement(getOnboardingState(), previousPlace);
    if (destination !== null) {
      answerOnboardingAnnouncement(destination);
    }
  }, [place]);
}
